import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';

interface InstallPWAProps {
  className?: string;
}

const InstallPWA: React.FC<InstallPWAProps> = ({ className }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      // Show the install prompt
      setShowInstallPrompt(true);
    };

    // Listen for the appinstalled event
    const handleAppInstalled = () => {
      setSnackbarMessage('Knoowl has been installed successfully!');
      setShowSnackbar(true);
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setSnackbarMessage('Thank you for installing Knoowl!');
      setShowSnackbar(true);
    } else {
      setSnackbarMessage('Installation was cancelled. You can install later from your browser menu.');
      setShowSnackbar(true);
    }

    setShowInstallPrompt(false);
    setDeferredPrompt(null);
  };

  const handleClosePrompt = () => {
    setShowInstallPrompt(false);
  };

  const handleCloseSnackbar = () => {
    setShowSnackbar(false);
  };

  // Only show on mobile devices
  if (!isMobile) return null;

  return (
    <>
      <AnimatePresence>
        {showInstallPrompt && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.3 }}
          >
            <Box
              sx={{
                position: 'fixed',
                top: '80px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1001,
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                border: `1px solid ${theme.palette.divider}`,
                p: 2,
                maxWidth: '90vw',
                width: '400px',
                textAlign: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DownloadIcon color="primary" />
                  <Box
                    component="img"
                    src="/favicon-owl.png"
                    alt="Knoowl"
                    sx={{ height: 24, width: 'auto' }}
                  />
                </Box>
                <Button
                  size="small"
                  onClick={handleClosePrompt}
                  sx={{ minWidth: 'auto', p: 0.5 }}
                >
                  <CloseIcon fontSize="small" />
                </Button>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Box
                  component="h3"
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 600,
                    mb: 1,
                    color: 'text.primary',
                    fontFamily: '"Kievit", "Roboto", sans-serif',
                  }}
                >
                  Install Knoowl
                </Box>
                <Box
                  component="p"
                  sx={{
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                    mb: 2,
                    fontFamily: '"Kievit", "Roboto", sans-serif',
                  }}
                >
                  Add Knoowl to your home screen for quick access and offline learning.
                </Box>
              </Box>

              <Button
                variant="contained"
                onClick={handleInstallClick}
                startIcon={<DownloadIcon />}
                sx={{
                  width: '100%',
                  py: 1,
                  fontWeight: 500,
                  fontFamily: '"Kievit", "Roboto", sans-serif',
                }}
              >
                Install App
              </Button>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default InstallPWA; 